import { useEffect, useState } from 'react';
import {
  DndContext,
  pointerWithin,
  rectIntersection,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

import { SortableItem } from './SortableItem';
import { BlocksPreview } from './BlocksPreview';
import { Information } from './Information';
import { Item } from './Item';
import './darkmode.css';
import './App.css';
import './gridsystem.css';
import './information.css';
import './dummymodule.css';

function App() {
  const initialList = [
    { id: 'a', title: 'a', extended: false, empty: false },
    { id: 'b', title: 'b', extended: false, empty: false },
    { id: 'c', title: 'c', extended: false, empty: false },
    { id: 'd', title: 'd', extended: false, empty: false },
    { id: 'e', title: 'e', extended: false, empty: false },
    { id: 'f', title: 'f', extended: false, empty: false },
    { id: 'g', title: 'g', extended: false, empty: false },
    { id: 'h', title: 'h', extended: false, empty: false },
  ];
  const rows = 2;
  const animDuration = 167;
  const gridSystemMinWidth4Col = 924;
  const gridSystemMinWidth3Col = 690;
  const gridSystemMinWidth2Col = 456;
  const [columns, setColumns] = useState(1);
  const [activeId, setActiveId] = useState(null);
  const [items, setItems] = useState([]);
  const [discardedItems, setDiscardedItems] = useState([]);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const [previewOverId, setPreviewOverId] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const customCollisionDetectionAlgorithm = (args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    return rectIntersection(args);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    startLivePreviewing(active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    const oldIndex = items.findIndex((item) => item.id === active?.id);
    const newIndex = items.findIndex((item) => item.id === over?.id);
    if (over?.id !== previewOverId) {
      setPreviewId(active?.id);
      setPreviewOverId(over?.id);
      setPreviewItems(
        oldIndex === newIndex ? items : getMovedList(items, oldIndex, newIndex),
      );
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      setItems(previewItems);
    }
    setActiveId(null);
    setTimeout(() => {
      endLivePreviewing();
    }, animDuration);
  };

  const handleResize = () => {
    const getLiveColCount = () => {
      const gridWidth = document.querySelector('.gridSystem').offsetWidth;
      if (gridWidth > gridSystemMinWidth4Col) {
        return 4;
      } else if (gridWidth > gridSystemMinWidth3Col) {
        return 3;
      } else if (gridWidth > gridSystemMinWidth2Col) {
        return 2;
      } else {
        return 1;
      }
    };
    const liveColCount = getLiveColCount();
    if (liveColCount !== columns) {
      setColumns(liveColCount);
    }
  };

  const startLivePreviewing = (id) => {
    setPreviewId(id);
  };

  const endLivePreviewing = () => {
    setPreviewId(null);
  };

  const addBlock = () => {
    const newList = [...items];
    const addedItemIdx = newList.findLastIndex((item) => item.empty); // Find index of last "deleted" item
    newList.unshift(newList.splice(addedItemIdx, 1)[0]);
    newList[0].empty = false;
    newList[0].extended = false;
    newList[0].title = newList[0].id;
    setItems(extendBlocks(newList));
  };

  const resetBlocks = () => {
    setItems(initialList);
  };

  const removeBlock = (removeId) => {
    let emptiedItems = [...items];
    const removedItem = emptiedItems.find((item) => item.id === removeId);
    removedItem.empty = true;
    removedItem.title = 'x';
    if (removedItem.extended) {
      emptiedItems = getMovedList(emptiedItems);
    }
    setItems(extendBlocks(emptiedItems));
  };

  const arrangeSections = (list) => {
    return list;
  };

  const colCount = (cols) => {
    return Math.ceil(cols / rows);
  };

  const getRow = (idx) => {
    return Math.floor(idx / columns);
  };

  const getSortedList = (list) => {
    const noEmptiesList = list.filter((blocks) => !blocks.empty);
    const emptiesList = list.filter((blocks) => blocks.empty);
    return [...noEmptiesList, ...emptiesList];
  };

  const findCorrespondingEmpty = (list, extId) => {
    const theExtIdx = list.findIndex((item) => item.id === extId);
    return list[theExtIdx + columns];
  };

  const findCorrespondingExtended = (list, emptyId) => {
    const theEmptyIdx = list.findIndex((item) => item.id === emptyId);
    return list[theEmptyIdx - columns];
  };

  const getEmpties = (list) => {
    return list.flatMap((item, i) => (item.empty === true ? i : []));
  };

  const arraySwap = (list, idx1, idx2) => {
    return list.with(idx1, list[idx2]).with(idx2, list[idx1]);
  };

  const getMovedList = (list, oldIdx = 0, newIdx = 0) => {
    // First, if an extended block is moving to the bottom row, no it isn't.
    newIdx =
      list[oldIdx]?.extended && newIdx >= columns ? newIdx - columns : newIdx;
    let movedList = arrayMove(list, oldIdx, newIdx);
    if (movedList.some((moveditem) => moveditem?.empty)) {
      // Transfer all extended blocks to the top row
      if (movedList[columns].extended) {
        // Find the rightmost non-extended block, and move it to the bottom row.
        let topRow = movedList.slice(0, columns);
        const overflowBlockIdx = topRow.findLastIndex(
          (rowblock) => !rowblock.extended,
        );
        movedList = arrayMove(movedList, overflowBlockIdx, columns + 1);
      }
      // Transfer empty blocks to positions directly below extended blocks
      let sortedList = getSortedList(movedList);
      sortedList.forEach((sorteditem, index) => {
        if (sorteditem.extended) {
          const correspondingEmptyId = findCorrespondingEmpty(
            list,
            sorteditem.id,
          ).id;
          const sortedEmptyIdx = sortedList.findIndex(
            (sortitem) => sortitem.id === correspondingEmptyId.id,
          );
          sortedList = arrayMove(sortedList, sortedEmptyIdx, index + columns);
        }
      });
      movedList = sortedList;
    }
    return movedList;
  };

  const extendBlocks = (list) => {
    // Assigns the "extended" flag to any block directly above an empty block.
    let extendedList = [...list];
    let emptiesIndexes = getEmpties(extendedList);
    if (emptiesIndexes.length > 0) {
      emptiesIndexes.forEach((emptyIdx) => {
        if (getRow(emptyIdx) === 0) {
          extendedList = arraySwap(extendedList, emptyIdx, emptyIdx + columns);
        }
      });
    }
    return extendedList.map((item, index) => {
      item.extended = extendedList[index + columns]?.empty;
      return item;
    });
  };

  const getRepopulatedItems = (num) => {
    if (num === items.length) {
      return items;
    }
    if (num < items.length) {
      const sortedItems = getSortedList(items);
      setDiscardedItems([...discardedItems, ...sortedItems.slice(num)]);
      return sortedItems.slice(0, num);
    } else {
      const diff = num - items.length;
      const restoredDiscards = discardedItems.slice(0, diff);
      const mergedItems = [...items, ...restoredDiscards];
      setDiscardedItems(discardedItems.slice(diff));
      return mergedItems;
    }
  };

  const toggleInfo = () => {
    setInfoVisible((infoVisible) => !infoVisible);
  };

  useEffect(() => {
    setPreviewItems(items);
  }, [items]);

  useEffect(() => {
    if (items.length !== columns * rows) {
      setItems(extendBlocks(getRepopulatedItems(columns * rows)));
    }
  }, [columns]);

  useEffect(() => {
    setItems(arrangeSections(initialList));
    setColumns(colCount(initialList.length));
    window.addEventListener('resize', handleResize, false);
  }, []);

  return (
    <>
      <header>
        <section className="hed-bar">
          <h1>Drag & Drop Prototype: Flow</h1>
        </section>
        <section className="button-bar">
          <button
            className="add-block"
            onClick={addBlock}
            disabled={!items.some((item) => item.empty)}
          >
            Add new block
          </button>
          <button className="reset-blocks" onClick={resetBlocks}>
            Reset
          </button>
        </section>
      </header>
      <Information />
      <main className="gridSystem" data-preview={previewId}>
        <div className="gridContainer"></div>
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetectionAlgorithm}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div
            className="sections"
            data-colcount={columns}
            data-blockcount={items.filter((item) => !item.empty).length}
          >
            <SortableContext items={items} strategy={rectSortingStrategy}>
              {items.map((item) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  extended={item.extended}
                  empty={item.empty}
                  onRemove={removeBlock}
                />
              ))}
            </SortableContext>
          </div>
          <DragOverlay
            dropAnimation={{
              duration: animDuration,
              easing: 'cubic-bezier(0.25,0,0,1)',
            }}
          >
            {activeId ? <Item id={activeId} /> : null}
          </DragOverlay>
        </DndContext>
        <BlocksPreview
          previewid={previewId}
          colcount={columns}
          blockcount={items.filter((item) => !item.empty).length}
          previewitems={previewItems}
        />
      </main>
    </>
  );
}

export default App;
